import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getDocument,
  GlobalWorkerOptions,
  type PDFDocumentProxy,
  type RenderTask,
} from 'pdfjs-dist'

// public/pdf.worker.min.mjs — proven to work in Electron/Cursor (workerPort/?worker hangs render)
GlobalWorkerOptions.workerPort = null
GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'

interface PdfCanvasViewerProps {
  url: string
  title: string
  className?: string
  style?: CSSProperties
}

/**
 * Renders a PDF via pdf.js canvases.
 * Native Chrome/Electron PDF plugins are unreliable inside iframes (esp. Cursor/Electron).
 */
export default function PdfCanvasViewer({ url, title, className, style }: PdfCanvasViewerProps) {
  const { t } = useTranslation('common')
  const containerRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    let pdf: PDFDocumentProxy | null = null
    const renderTasks: RenderTask[] = []

    async function render() {
      setLoading(true)
      setError(null)

      let container = containerRef.current
      if (!container) {
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
        container = containerRef.current
      }
      if (!container || !alive) {
        if (alive) setLoading(false)
        return
      }
      container.replaceChildren()

      try {
        const response = await fetch(url, { credentials: 'include', cache: 'no-store' })
        if (!response.ok) throw new Error(`PDF HTTP ${response.status}`)
        const data = await response.arrayBuffer()
        if (!alive) return

        pdf = await getDocument({ data }).promise
        if (!alive) return

        const cssWidth = Math.max(container.clientWidth - 16, 320)

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (!alive) return
          const page = await pdf.getPage(pageNum)
          const baseViewport = page.getViewport({ scale: 1 })
          const scale = Math.min(cssWidth / baseViewport.width, 2)
          const viewport = page.getViewport({ scale })
          const outputScale = Math.min(window.devicePixelRatio || 1, 2)

          const canvas = document.createElement('canvas')
          canvas.width = Math.floor(viewport.width * outputScale)
          canvas.height = Math.floor(viewport.height * outputScale)
          canvas.style.width = `${Math.floor(viewport.width)}px`
          canvas.style.height = `${Math.floor(viewport.height)}px`
          canvas.className = 'mb-4 mx-auto block bg-white shadow-sm'
          canvas.setAttribute('aria-label', `${title} — ${pageNum}/${pdf.numPages}`)

          const ctx = canvas.getContext('2d', { alpha: false })
          if (!ctx) throw new Error('Canvas 2D unavailable')
          const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined

          container.appendChild(canvas)

          const task = page.render({
            canvasContext: ctx,
            viewport,
            transform,
            background: 'white',
          })
          renderTasks.push(task)
          await task.promise
        }
      } catch (err) {
        if (alive) {
          console.error('PDF render failed', err)
          setError('pdfLoadError')
        }
      } finally {
        if (alive) setLoading(false)
      }
    }

    void render()

    return () => {
      alive = false
      for (const task of renderTasks) {
        try {
          task.cancel()
        } catch {
          /* ignore */
        }
      }
      void pdf?.destroy()
    }
  }, [url, title])

  return (
    <div className={`relative ${className ?? ''}`} style={style}>
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-100 text-sm text-gray-500">
          {t('loading')}
        </div>
      )}
      {error && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-gray-100 text-sm text-red-600">
          <span>{t(error)}</span>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            {t('openInNewTab')}
          </a>
        </div>
      )}
      <div ref={containerRef} className="h-full overflow-y-auto bg-gray-100 p-2" />
    </div>
  )
}
